// @ts-nocheck
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import CueStyleBox from 'components/common/closedCaptions/CueStyleBox';
import { usePlayerCaptions } from 'components/providers/PlayerStateContext';

import styles from './ClosedCaptionsOverlay.module.scss';

const MAX_LINE_COUNT = 30;

// W3C documentation:
// https://www.w3.org/TR/webvtt1/

const ClosedCaptionsOverlay = ({ className }) => {
  const styleSettings = {
    textSize: useSelector(state => state.closedCaptionSettings.textSize),
    textColor: useSelector(state => state.closedCaptionSettings.textColor),
    textType: useSelector(state => state.closedCaptionSettings.textType),
    textOpacity: useSelector(state => state.closedCaptionSettings.textOpacity),
    textShadow: useSelector(state => state.closedCaptionSettings.textShadow),
    textShadowColor: useSelector(
      state => state.closedCaptionSettings.textShadowColor,
    ),
    backgroundColor: useSelector(
      state => state.closedCaptionSettings.backgroundColor,
    ),
    backgroundOpacity: useSelector(
      state => state.closedCaptionSettings.backgroundOpacity,
    ),
    windowColor: useSelector(state => state.closedCaptionSettings.windowColor),
    windowOpacity: useSelector(
      state => state.closedCaptionSettings.windowOpacity,
    ),
  };

  const cues = usePlayerCaptions();
  const containerRef = useRef();
  const html = [];

  // Font size calculation requires container height which requiring a re-render
  const [containerHeight, setContainerHeight] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const { offsetHeight } = containerRef.current;

    // To prevent re-render, only update containerHeight when the value is changed
    if (containerHeight !== offsetHeight) setContainerHeight(offsetHeight);
  });

  const fontSize = Math.round(containerHeight / MAX_LINE_COUNT);

  // how to calculate line position:
  // https://www.w3.org/TR/webvtt1/#cues
  const calculateLinePos = cue => {
    if (
      typeof cue.line === 'number' &&
      (cue.snapToLines || (cue.line >= 0 && cue.line <= 100))
    ) {
      return cue.line;
    }
    if (!cue.snapToLines) return 100;
    if (
      !cue.track ||
      !cue.track.textTrackList ||
      !cue.track.textTrackList.mediaElement
    ) {
      return -1;
    }
    const { track } = cue;
    const trackList = track.textTrackList;
    let n = 0;
    for (let i = 0; i < trackList.length && trackList[i] !== track; i += 1) {
      if (trackList[i].mode === 'showing') {
        n += 1;
      }
    }
    n += 1;
    return -n;
  };

  let lastLinePosition = -1;

  const cueBoxPositions = {};
  Object.keys(cues).forEach(key => {
    const cue = cues[key];
    const linePos = calculateLinePos(cue);
    const { vertical, positionAlign, position } = cue;
    let { size } = cue;
    let textPos;
    switch (positionAlign) {
      case 'center':
        textPos = position - size / 2;
        break;
      case 'end':
        textPos = position - size;
        break;
      case 'start':
      default:
        textPos = position;
    }
    if (!(typeof textPos === 'number')) {
      textPos = 0;
    }
    size = textPos + size > 100 ? 100 - textPos : size;
    const line = linePos < 0 ? linePos + MAX_LINE_COUNT : linePos;
    if (line > lastLinePosition) lastLinePosition = line;

    const style =
      vertical === ''
        ? {
            marginLeft: `${textPos}%`,
            width: `${size}%`,
          }
        : {
            topLeft: `${textPos}%`,
            height: `${size}%`,
          };
    const cueBox = (
      <CueStyleBox
        className={styles.textLine}
        cue={cue}
        key={`cuebox-${cue.text}`}
        settings={styleSettings}
        style={style}
      />
    );

    cueBoxPositions[line] = cueBox;
  });

  const spacerHeight = fontSize + containerHeight * 0.02; // font size + top & bottom margin
  let spacerCount = 0;
  for (let i = 0; i <= lastLinePosition; i += 1) {
    const cueBox = cueBoxPositions[i];
    if (cueBox) {
      spacerCount &&
        html.push(
          <div
            style={{ height: spacerHeight * spacerCount }}
            key={`cuebox-spacer-${i}`}
          />,
        );
      html.push(cueBox);
      spacerCount = 0;
    } else spacerCount += 1;
  }

  return (
    <div
      id="closed-caption-overlay"
      className={classNames(styles.overlay, className)}
      data-testid="closed-caption-overlay"
      ref={containerRef}
      style={{ fontSize: `${fontSize}px` }}
    >
      {html}
    </div>
  );
};

ClosedCaptionsOverlay.propTypes = {
  className: PropTypes.string,
};

ClosedCaptionsOverlay.defaultProps = {
  className: null,
};

export default React.memo(ClosedCaptionsOverlay);
